<template>
  <BasePhoneRow
    :id="'item-' + tender.id"
    class="tender-list-item"
    :class="{ 'is-unread': isUnread }"
    :href="href"
    :selectable="selectable"
    :selected="selected"
    @open="$emit('open')"
    @toggle-select="$emit('toggle-select')"
  >
    <template #title>
      <span class="tender-list-item__title">
        <TextHighlight class="base-phone-row__title" :content="tender.title" />
        <!-- At the end of the title's line, as in the notifications: at its start, it read as the
             platform's icon desktop rows show there. -->
        <span v-if="isUnread" class="tender-list-item__dot" aria-hidden="true"></span>
      </span>
    </template>

    <p v-if="emitterLine" class="base-phone-row__line tender-list-item__emitter">{{ emitterLine }}</p>

    <div class="base-phone-row__meta">
      <BaseTag v-if="countdown" icon="clock" preset="soft" :tone="countdownTone">{{ countdown }}</BaseTag>
      <TenderStatusTag v-else :status="tender.status" />

      <template v-if="tender.matchedKeywords">
        <BaseTag v-if="tender.matchedKeywords.length > 0" tone="info" preset="secondary" icon="type">
          {{ tender.matchedKeywords.length }}
        </BaseTag>
        <BaseTag v-else tone="warning" preset="secondary" icon="type">
          {{ $t('page.tenders.not-covered-by-keywords') }}
        </BaseTag>
      </template>

      <span
        v-if="showScore && tender.score !== null"
        class="tender-list-item__score"
        role="img"
        :aria-label="scoreLabel"
      >
        <DynamicThermometer :value="tender.score" />
      </span>

      <BaseTag v-if="tender.opportunities && tender.opportunities.length" icon="target" tone="neutral" preset="soft">
        {{ $t('page.opportunity.opportunity') }}
      </BaseTag>
      <span
        v-if="tender.assignees && tender.assignees.length"
        class="tender-list-item__indicator"
        :aria-label="$t('page.tenders.data-table-titles.assignees')"
      >
        <BaseIcon name="users" aria-hidden="true" />{{ tender.assignees.length }}
      </span>

      <span class="tender-list-item__platform">{{ getPlatformName(tender.source.id) }}</span>
    </div>

    <p v-if="archivedLabel" class="tender-list-item__archived">{{ archivedLabel }}</p>

    <template v-if="!selectable" #actions>
      <button
        type="button"
        class="base-phone-row__action"
        :class="{ 'is-active': isFollowed }"
        :aria-label="isFollowed ? $t('actions.unfollow') : $t('actions.follow')"
        :aria-pressed="String(isFollowed)"
        @click.stop="$emit('follow')"
      >
        <BaseIcon name="bookmark" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="base-phone-row__action"
        :aria-label="$t('actions.more-actions')"
        aria-haspopup="dialog"
        @click.stop="$emit('more')"
      >
        <BaseIcon name="more-horizontal" aria-hidden="true" />
      </button>
    </template>
  </BasePhoneRow>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import TenderStatusTag from '@/components/page-tender-details/TenderStatusTag.vue';
import TextHighlight from '@/components/text-highlight/TextHighlight';
import DynamicThermometer from '@/components/util/DynamicThermometer';
import PlatformName from '@/mixins/platform-name-mixin';
import TenderScoreMixin from '@/mixins/tender-score-mixin';
import { getDhm } from '@/util/date-util';
import { paths } from '@/util/paths';
import { getClosingCountdownTone, isClosingCountdownVisible } from '@/util/tender-closing-countdown';

// One tender in the phone list: what decides whether to open it. The rest is on its page.
export default {
  name: 'TenderListItem',
  mixins: [PlatformName, TenderScoreMixin],
  components: {
    BaseIcon,
    BasePhoneRow,
    BaseTag,
    DynamicThermometer,
    TenderStatusTag,
    TextHighlight,
  },
  props: {
    tender: {
      type: Object,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showScore: {
      type: Boolean,
      default: false,
    },
    archivedLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    href() {
      return paths.tenderDetails.replace(':id', this.tender.id);
    },
    isUnread() {
      return !!this.tender.userTender && !this.tender.userTender.read;
    },
    isFollowed() {
      return !!this.tender.userTender && !!this.tender.userTender.followed;
    },
    emitterLine() {
      const emitter = this.tender.emitter;
      if (!emitter) return '';
      const locality = emitter.address && emitter.address.locality;
      return locality ? `${emitter.name} · ${locality}` : emitter.name;
    },
    // Days (and hours) left, as on the desktop table; otherwise the status takes its place.
    countdown() {
      const left = this.tender.daysBeforeClosing;
      if (!left || !isClosingCountdownVisible(this.tender.status)) return '';
      return getDhm(left).split(' ')[0];
    },
    countdownTone() {
      return getClosingCountdownTone(this.tender.daysBeforeClosing);
    },
    // The desktop table tells the score in a hover tooltip, which a touch screen never shows.
    scoreLabel() {
      const level = this.$t(`search-filters.relevance-score.${this.getScoreClass(this.tender.score)}`);
      return `${this.$t('search-filters.relevance-score.name')} : ${level}`;
    },
  },
};
</script>

<style lang="scss">
// The row is BasePhoneRow's; here, what only a tender shows.
.tender-list-item {
  &__title {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    .base-phone-row__title {
      flex: 1;
      min-width: 0;
    }
  }

  // Centred on the title's first line.
  &__dot {
    flex: none;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: $radius-full;
    background: $color-accent;
  }

  // Read tenders are lighter than the unread ones.
  .base-phone-row__title {
    color: $color-text-body;
  }

  &.is-unread .base-phone-row__title {
    font-weight: 700;
    color: $color-normal;
  }

  // The thermometer's squares are positioned absolutely, one 8px above and one 8px below
  // the middle one. This box holds all three, the height of the tags beside it.
  &__score {
    position: relative;
    flex: none;
    width: 12px;
    height: 22px;

    .modern-thermo-icon__container {
      width: 12px;
      height: 22px;
    }

    .modern-thermo-icon {
      top: 8px;
      left: 3px;
      margin-top: 0;
    }
  }

  &__indicator {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    color: $color-slate-500;

    .base-icon,
    &.base-icon {
      width: 14px;
      height: 14px;
    }
  }

  &__platform {
    font-size: 12px;
    color: $color-slate-500;
  }

  &__archived {
    margin: 6px 0 0;
    font-size: 12px;
    color: $color-slate-500;
  }
}
</style>
